/*
 * Copyright (C) 2025 Musarubra US LLC.  All Rights Reserved.
 */

import { GenericWidgetContext } from './generic';
import { iamLogin, iamUtils } from '../main';

export abstract class EnrollPhoneWidgetContext extends GenericWidgetContext {
  protected abstract getRequestButtons(): HTMLElement[];
  private mutationObservers: MutationObserver[] = [];
  private persistentElementListeners: { element: HTMLElement | Window, type: string, listener: EventListenerOrEventListenerObject }[] = [];
  private containerResizeObserver: ResizeObserver;
  private dropdownPopup: HTMLElement; // appended to the body, so deinitialize() must remove it

  private registerMutationObserver(target: HTMLElement, options: MutationObserverInit, callback: MutationCallback) {
    const mutationObserver = new MutationObserver(callback);
    mutationObserver.observe(target, options);
    this.mutationObservers.push(mutationObserver);
  }

  private registerPersistentElementListener(element: HTMLElement | Window, type: string, listener: EventListenerOrEventListenerObject) {
    this.persistentElementListeners.push({ element, type, listener });
    element.addEventListener(type, listener);
  }

  public override initialize() {
    super.initialize();
    this.getRequestButtons().forEach(button => {
      this.registerMutationObserver(button, { attributeFilter: ['class'], attributeOldValue: true }, (mutationList, observer) => {
        for (let i = 0; i < mutationList.length; i++) {
          // button's class has changed, which is where "disabled" state is tracked
          if (button.style.display !== 'none') {
            // button is visible (otherwise we should ignore)
            const isDisabled = button.className.includes('disabled');
            const wasDisabled = mutationList[i].oldValue.includes('disabled');
            if (isDisabled !== wasDisabled) {
              if (isDisabled) {
                if ((this.getRequestButtons() as Element[]).includes(document.activeElement) || document.activeElement === this.elements.getPhoneField()) {
                  // send code button must've just been pressed or enter key hit from the phone number field
                  // which means the user will want to enter the pass code next
                  this.elements.getPasscodeField().focus();
                }
                button.tabIndex = -1; // shouldn't allow focus when disabled
              } else {
                button.tabIndex = undefined; // should allow focus when enabled
              }
            }
          }
        }
      });
    });

    // okta widget no longer styles the country dropdown - it's just a plain jane select element, so generate a styled dropdown to mirror the select element,
    // and send the hidden select element "change" events to keep it in sync. this closely follows the dropdown used for keycloak.
    const dropdownField = this.elements.getCountryDropdownField();
    const oktaSelect = dropdownField.querySelector('select');

    // out of an abundance of caution, do a quick test to confirm that programmatically changing a select element's value and dispatching a change
    // event works as expected in the current browser. if not, we'll have to leave the okta select element in place and not show our own dropdown.
    let testSelect = document.createElement('select');
    let options = [document.createElement('option'), document.createElement('option')];
    options[0].value = 'a';
    options[1].value = 'b';
    options.forEach(option => testSelect.appendChild(option));
    testSelect.selectedIndex = 0;
    let eventHandled = false;
    testSelect.addEventListener('change', () => eventHandled = true);
    testSelect.value = 'b';
    testSelect.dispatchEvent(new Event('change', { bubbles: true }));
    if (testSelect.selectedIndex === 1 && eventHandled) {
      // create elements...
      const dropdownControl = document.createElement('div');
      const dropdownAnchor = document.createElement('a');
      const dropdownSelectionText = document.createElement('span');
      const dropdownArrow = document.createElement('span');
      const dropdownPopup = document.createElement('div');
      const dropdownSearch = document.createElement('div');
      const dropdownInput = document.createElement('input');
      const dropdownList = document.createElement('ul');
      dropdownControl.className = 'droplist';
      dropdownControl.id = 'countryPicker';
      dropdownControl.tabIndex = 0;
      dropdownAnchor.href = 'javascript:;';
      dropdownAnchor.tabIndex = -1;
      dropdownPopup.className = 'droplistPopup';
      dropdownSearch.className = 'search';
      dropdownInput.type = 'text';
      dropdownInput.id = 'countrySearch';
      dropdownInput.setAttribute('autocomplete', 'off');
      dropdownList.className = 'dropItems';
      dropdownControl.appendChild(dropdownAnchor);
      dropdownAnchor.appendChild(dropdownSelectionText);
      dropdownAnchor.appendChild(dropdownArrow);
      dropdownPopup.appendChild(dropdownSearch);
      dropdownSearch.appendChild(dropdownInput);
      dropdownPopup.appendChild(dropdownList);

      // handle resizing and repositioning so that the popup stays aligned with the main control...
      const resizeHandler = () => {
        if (dropdownControl.classList.contains('open')) {
          const pos = iamUtils.getRelativePosition(dropdownControl, document.body);
          dropdownPopup.style.top = pos.top + pos.height + 'px';
          dropdownPopup.style.left = pos.left + 'px';
          dropdownPopup.style.width = pos.width + 'px';
        }
      };
      this.registerPersistentElementListener(window, 'resize', () => resizeHandler()); // resizing the browser window moves the control
      // dom changes within the widget can move the control (like showing or hiding of red "required field" text)
      this.containerResizeObserver = new ResizeObserver(() => resizeHandler());
      this.containerResizeObserver.observe(this.elements.getContainer());

      // handle toggling the visibility of the countries popup...
      const toggleCountriesPopup = (options: { forceClose?: boolean, allowFocusLoss?: boolean } = {}) => {
        if (!options.forceClose && dropdownControl.getAttribute('disabled') !== 'true' && dropdownPopup.style.display !== 'block') {
          // opening
          dropdownControl.classList.add('open');
          // offscreen until after dom has processed the showing of it, so the position calculation is correct
          dropdownPopup.style.top = '-9999px';
          dropdownPopup.style.left = '-9999px';
          dropdownPopup.style.display = 'block';
          setTimeout(() => {
            const pos = iamUtils.getRelativePosition(dropdownControl, document.body);
            dropdownPopup.style.top = pos.top + pos.height + 'px';
            dropdownPopup.style.left = pos.left + 'px';
            dropdownPopup.style.width = pos.width + 'px';
            dropdownInput.focus();
            if (selectedCountryElem) {
              // make sure the selected country is visible in the popup
              selectedCountryElem.scrollIntoView({ block: 'nearest' });
              selectedCountryElem.classList.add('focused');
            }
          }, 0);
        } else {
          // closing
          dropdownList.querySelector('li.focused')?.classList.remove('focused');
          dropdownControl.classList.remove('open');
          dropdownPopup.style.display = 'none';
          if (!options.allowFocusLoss) {
            dropdownControl.focus();
          }
        }
      };
      dropdownControl.addEventListener('click', () => toggleCountriesPopup());
      // close the popup if it's open and the user clicks outside of it
      this.registerPersistentElementListener(document.body, 'click', event => {
        // clicking the droplist or any element within it shouldn't close it
        if (event.target instanceof Element && event.target.closest('.droplist') === null && event.target.closest('.droplistPopup') === null && dropdownPopup.style.display === 'block') {
          // clicking outside the open picker, so close it
          toggleCountriesPopup({ forceClose: true, allowFocusLoss: true });
        }
      });

      // handle country selection...
      const selectCountry = (countryElem: Element) => {
        dropdownSelectionText.textContent = countryElem.textContent;
        oktaSelect.value = countryElem.getAttribute('value');
        oktaSelect.dispatchEvent(new Event('change', { bubbles: true })); // keep okta's element in sync, which will update the country code next to the phone number
        countryElem.className = 'selected';
        if (selectedCountryElem && selectedCountryElem !== countryElem) {
          selectedCountryElem.className = '';
        }
        selectedCountryElem = countryElem;
      };
      dropdownList.addEventListener('click', event => {
        if (event.target instanceof Element && event.target !== noMatchesElem) {
          selectCountry(event.target);
          toggleCountriesPopup({forceClose: true});
        }
      });
      dropdownControl.addEventListener('keydown', event => {
        if ((event.key === 'Enter' || (event.key === ' ' && dropdownPopup.style.display !== 'block')) && !(event.ctrlKey || event.metaKey || event.shiftKey || event.altKey)) {
          toggleCountriesPopup();
          event.preventDefault();
          event.stopPropagation();
        }
      });

      // handle keyboard events...
      // typing in the popup's search control should filter the list, and arrowing around should change the selection
      let noMatchesElem: HTMLLIElement;
      iamUtils.addEventListeners(dropdownInput, ['keydown', 'keyup', 'change', 'paste'], event => {
        if (event.type === 'keydown' && event instanceof KeyboardEvent && !(event.ctrlKey || event.metaKey || event.altKey)) {
          if (event.key === 'Tab') {
            toggleCountriesPopup({ forceClose: true });
            if (event.shiftKey) {
              event.preventDefault();
            } // else shift focus to main control, then let the browser's default key handler tab to the next control
            return;
          } else if (!event.shiftKey) {
            const navigate = (event: KeyboardEvent, nextChooser: (index: number) => number) => {
              const children = countryElems.filter(li => li.style.display !== 'none');
              const prev = dropdownList.querySelector<HTMLElement>('li.focused');
              let index = prev ? Array.from(children).indexOf(prev) : -1;
              index = Math.min(Math.max(0, nextChooser(index)), children.length - 1);
              prev?.classList.remove('focused');
              const next = children[index];
              if (next) {
                next.classList.add('focused');
                next.scrollIntoView({ block: 'nearest' });
              }
              event.preventDefault();
              event.stopPropagation();
            };
            switch (event.key) {
              case 'ArrowDown':
                navigate(event, index => index + 1);
                break;
              case 'ArrowUp':
                navigate(event, index => index - 1);
                break;
              case 'Enter': {
                const current = dropdownList.querySelector('li.focused');
                if (current) {
                  selectCountry(current);
                }
                toggleCountriesPopup({ forceClose: true });
                return;
              }
              case 'Escape':
                if (!dropdownInput.value) {
                  toggleCountriesPopup({ forceClose: true });
                } else {
                  dropdownInput.value = '';
                }
                break;
            }
          }
        }
        setTimeout(() => {
          // show or hide countries according to filter text
          let matches = 0;
          countryElems.forEach(countryElem => {
            if (countryElem.textContent.toLowerCase().indexOf(dropdownInput.value.toLowerCase()) >= 0) {
              countryElem.style.display = '';
              matches++;
            } else {
              countryElem.style.display = 'none';
            }
          });
          if (matches === 0) {
            if (!noMatchesElem) {
              noMatchesElem = document.createElement('li');
              noMatchesElem.className = 'noMatches';
              dropdownList.appendChild(noMatchesElem);
            }
            iamLogin.localizeElement(noMatchesElem, { stringKey: 'noMatches', stringParameters: { text: dropdownInput.value } });
          } else if (matches > 0 && noMatchesElem) {
            dropdownList.removeChild(noMatchesElem);
            noMatchesElem = null;
          }
        }, 0); // asynchronous because the paste event fires before content is pasted into the field
      });

      // initialize dropdown...
      const oktaOptions = oktaSelect.querySelectorAll('option');
      let selectedCountry = oktaOptions[oktaSelect.selectedIndex].value || 'US';
      let selectedCountryElem: Element;
      dropdownSelectionText.textContent = oktaOptions[oktaSelect.selectedIndex].textContent;
      // populate the list of countries
      oktaOptions.forEach(option => {
        const countryElem = document.createElement('li');
        countryElem.textContent = option.textContent;
        const value = option.getAttribute('value');
        countryElem.setAttribute('value', value);
        dropdownList.appendChild(countryElem);
        if (value === selectedCountry) {
          selectCountry(countryElem);
        }
      });
      const countryElems = Array.from(dropdownList.children) as HTMLElement[];
      dropdownField.appendChild(dropdownControl);
      document.body.appendChild(dropdownPopup); // popup needs to be at root of page so it doesn't get clipped by ancestors
      this.dropdownPopup = dropdownPopup;
    } else {
      // let the okta select element be used as-is
      oktaSelect.style.display = 'inline-block';
    }
    testSelect = options = null;
  }

  public override deinitialize() {
    this.mutationObservers.forEach(one => {
      one.disconnect();
    });
    this.persistentElementListeners.forEach(one => {
      one.element.removeEventListener(one.type, one.listener);
    });
    this.containerResizeObserver?.disconnect();
    // the popup isn't inside the widget, so it isn't removed along with the widget's view. without this, every visit
    // to the sms/call enrollment screen (e.g. back to the factor list and in again) leaves another popup in the body
    this.dropdownPopup?.remove();
  }

  public override setInitialFocus() {
    const field = this.elements.getPhoneField();
    if (field && !field.disabled) {
      field.focus();
    }
  }

  public override validateForm() {
    this.elements.getSubmitButton().disabled = this.elements.getPasscodeField().value.length === 0;
  }

  public override get supported() {
    return true;
  }
}
